import { useMutation } from "@apollo/client";
import Button from "components/base/Button";
import { Input } from "components/base/Form";
import { Alert } from "components/base/Toast";
import { CubeSizeInput } from "components/shared/ItemSizeInput";
import gql from "graphql-tag";
import { useContext, useEffect, useState } from "react";
import {
  PiArrowsClockwiseBold,
  PiCheckBold,
  PiImageBold,
  PiPackage,
  PiPencilSimpleBold,
  PiTrashBold,
} from "react-icons/pi";
import ShipmentContext from "../ShipmentContext";
import { FRAGMENT_SHIPMENT_TOTALS } from "./graphql";

const DELETE_SHIPMENT_INVOICE_ROW = gql`
  mutation DELETE_SHIPMENT_INVOICE_ROW($id: ID!) {
    deleteShipmentInvoiceRow(id: $id) {
      id
      shipment {
        ...shipmentTotals
      }
    }
  }
  ${FRAGMENT_SHIPMENT_TOTALS}
`;

const rowInputFrom = (row) => ({
  packingName: row.packingName || "",
  productName: row.productName || "",
  hsCode: row.hsCode || "",
  hsCodeForDestination: row.hsCodeForDestination || "",
  itemNumber: row.itemNumber || "",
  qtyPerCarton: row.qtyPerCarton || 0,
  netWeight: row.netWeight || 0,
  grossWeight: row.grossWeight || 0,
  cbm: row.cbm || 0,
  outerCartonX: row.outerCartonX || 0,
  outerCartonY: row.outerCartonY || 0,
  outerCartonZ: row.outerCartonZ || 0,
  customsName: row.customsName || "",
  customsBrand: row.customsBrand || "",
  customsUsage: row.customsUsage || "",
  customsValue: row.customsValue || 0,
  customsSize: row.customsSize || "",
  customsMaterials: row.customsMaterials || "",
  qty: row.qty || 0,
});

const trimValue = (value) => (typeof value === "string" ? value.trim() : value);

function ProductImage({ src }) {
  const [failed, setFailed] = useState(false);

  if (!src || failed) {
    return (
      <div className="flex w-[36px] h-[36px] items-center justify-center border border-dashed border-gray-200 text-gray-600 dark:border-gray-700 dark:text-gray-300">
        <PiImageBold size={16} />
      </div>
    );
  }

  return (
    <img
      alt=""
      className="product-image w-[36px] h-[36px] object-contain"
      src={src}
      onError={() => setFailed(true)}
    />
  );
}

function EditField({ id, label, children, className = "" }) {
  return (
    <div
      className={`flex min-w-0 flex-col items-stretch gap-1 sm:flex-row sm:items-center sm:gap-2 ${className}`}
    >
      <label
        htmlFor={id}
        className="shrink-0 text-sm font-medium text-gray-600 sm:min-w-[4.5rem] dark:text-gray-300"
      >
        {label}
      </label>
      <div className="min-w-0 sm:flex-1">{children}</div>
    </div>
  );
}

function ShipmentInvoiceRow({
  shipmentInvoiceId,
  row,
  archived,
  selectionControl,
}) {
  const {
    state,
    dispatch,
    prepareShippingMark,
    changeShipmentInvoiceRowProduct,
    updateShipmentInvoiceRow,
  } = useContext(ShipmentContext);
  const [showProductEditableData, setShowProductEditableData] = useState(false);
  const [rowInput, setRowInput] = useState(() => rowInputFrom(row));

  const [deleteShipmentInvoiceRow, deleteShipmentInvoiceRowRes] = useMutation(
    DELETE_SHIPMENT_INVOICE_ROW,
    {
      variables: { id: row.id },
      onCompleted: ({ deleteShipmentInvoiceRow }) => {
        Alert("success", "Product deleted");
        dispatch({
          type: "deleteShipmentInvoiceRow",
          payload: {
            shipmentInvoiceId,
            shipmentInvoiceRowId: row.id,
            shipment: deleteShipmentInvoiceRow.shipment,
          },
        });
      },
      onError: (error) => Alert("error", error.message),
    },
  );

  const isSpecial = !row.product || row.product.productType === "special";
  const sku = row.itemNumber;
  const fieldId = (field) => `shipment-invoice-row-${row.id}-${field}`;

  useEffect(() => {
    setRowInput(rowInputFrom(row));
  }, [row.id]);

  function saveRow(keys) {
    const values = Object.fromEntries(
      [keys].flat().map((key) => [key, trimValue(rowInput[key])]),
    );
    setRowInput((prev) => ({ ...prev, ...values }));
    updateShipmentInvoiceRow({
      shipmentInvoiceRowId: row.id,
      rowInput: values,
    });
  }

  async function copySku() {
    try {
      await navigator.clipboard.writeText(sku);
      Alert("success", "SKU copied");
    } catch (_) {
      Alert("error", "Could not copy SKU");
    }
  }

  let itemsPerSet = 1;
  let validQty = true;
  if (!isSpecial) {
    itemsPerSet = row.product.itemsPerSet;
    validQty = row.qty % itemsPerSet === 0;
  } else {
    itemsPerSet = row.qtyPerCarton; // for special item, use qtyPerCarton
    validQty = row.qty % itemsPerSet === 0;
  }

  return (
    <div
      className={`
         px-4 py-3 lg:px-8 lg:py-6 lg:whitespace-no-wrap relative transition-all duration-200
          ${showProductEditableData ? "mx-3 my-4 rounded-xl bg-gray-100 shadow-lg shadow-slate-200/60 dark:bg-gray-800 dark:shadow-none" : "after:absolute after:bottom-0 after:left-4 after:right-4 after:border-b after:border-gray-200 dark:after:border-gray-700 lg:after:left-8 lg:after:right-8"}
          ${archived ? "opacity-50 cursor-not-allowed [&_*]:cursor-not-allowed" : ""}
        `}
    >
      <div className="relative">
        <div className="flex flex-col gap-2 pr-36 lg:flex-row lg:items-center lg:gap-4">
          {(!isSpecial || !showProductEditableData) && (
            <>
              {!isSpecial && row.product.images.length > 0 && (
                <div className="flex">
                  {row.product.images.map(({ id, url }) => (
                    <ProductImage key={id} src={url} />
                  ))}
                </div>
              )}
              <div className="flex-1">
                <div className="lg:flex lg:items-center lg:gap-2">
                  <button
                    type="button"
                    title="Copy SKU"
                    aria-label={`Copy SKU ${sku}`}
                    className="cursor-pointer rounded-md bg-gray-100 px-2 py-0.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-200 active:bg-gray-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600 dark:active:bg-gray-500"
                    onClick={copySku}
                  >
                    {sku}
                  </button>
                  <div className="mt-1 flex items-center gap-2 font-bold lg:mt-0">
                    <span className="text-sm">{row.productName}</span>
                    {!isSpecial && (
                      <Button
                        icon={<PiPackage size={18} />}
                        aria-label="Prepare shipping mark"
                        title="Prepare shipping mark"
                        className="h-7 w-7 justify-center rounded-md hover:bg-sky-100 active:bg-sky-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 dark:hover:bg-sky-900/60 dark:active:bg-sky-800/70"
                        onClick={() => prepareShippingMark(state.customer, row)}
                      />
                    )}
                  </div>
                </div>
                {!showProductEditableData && (
                  <div className="mt-2 text-xs">{row.packingName}</div>
                )}
              </div>
            </>
          )}
          {validQty || (isSpecial && showProductEditableData) ? null : (
            <div className="hidden w-80 shrink-0 text-sm font-semibold text-red-600 lg:block">
              This product has {itemsPerSet} items / set, {row.qty} is not a
              valid number for whole package. Please make sure the qty is
              correct.
            </div>
          )}
        </div>

        {validQty || !isSpecial || !showProductEditableData ? null : (
          <div className="mt-[72px] text-sm font-semibold text-red-600 lg:mt-0 lg:pr-36">
            This product has {itemsPerSet} items / set, {row.qty} is not a valid
            number for whole package. Please make sure the qty is correct.
          </div>
        )}

        <div
          className={`
          transition-all duration-200
          ${showProductEditableData ? `show mr-36 px-4 py-4 lg:mt-4 ${isSpecial && validQty ? "mt-[72px]" : "mt-8"}` : "hidden"}
        `}
        >
          <div className="space-y-3">
            {isSpecial && (
              <div className="grid gap-6 md:grid-cols-2">
                <EditField id={fieldId("item-number")} label="Item Number">
                  <Input
                    id={fieldId("item-number")}
                    className="w-40 text-center"
                    type="text"
                    value={rowInput.itemNumber}
                    onChange={(e) =>
                      setRowInput((prev) => ({
                        ...prev,
                        itemNumber: e.target.value,
                      }))
                    }
                    onBlur={() => saveRow("itemNumber")}
                  />
                </EditField>
                <EditField id={fieldId("name")} label="Name">
                  <Input
                    id={fieldId("name")}
                    className="w-full max-w-xl"
                    type="text"
                    placeholder="Special Item Name"
                    value={rowInput.productName}
                    onChange={(e) =>
                      setRowInput((prev) => ({
                        ...prev,
                        productName: e.target.value,
                      }))
                    }
                    onBlur={() => saveRow("productName")}
                  />
                </EditField>
              </div>
            )}
            <EditField id={fieldId("packing-name")} label="Packing Name">
              <Input
                id={fieldId("packing-name")}
                className="w-full max-w-xl"
                type="text"
                placeholder="Packing Name"
                value={rowInput.packingName}
                onChange={(e) =>
                  setRowInput((prev) => ({
                    ...prev,
                    packingName: e.target.value,
                  }))
                }
                onBlur={() => saveRow("packingName")}
              />
            </EditField>
            <div className="grid gap-6 md:grid-cols-2">
              <EditField id={fieldId("hs-code")} label="HS Code for China">
                <Input
                  id={fieldId("hs-code")}
                  className="w-36 text-center"
                  type="text"
                  value={rowInput.hsCode}
                  onChange={(e) =>
                    setRowInput((prev) => ({ ...prev, hsCode: e.target.value }))
                  }
                  onBlur={() => saveRow("hsCode")}
                />
              </EditField>
              <EditField
                id={fieldId("hs-code-destination")}
                label="HS Code for Destination"
              >
                <Input
                  id={fieldId("hs-code-destination")}
                  className="w-44 text-center"
                  type="text"
                  value={rowInput.hsCodeForDestination}
                  onChange={(e) =>
                    setRowInput((prev) => ({
                      ...prev,
                      hsCodeForDestination: e.target.value,
                    }))
                  }
                  onBlur={() => saveRow("hsCodeForDestination")}
                />
              </EditField>
            </div>
          </div>

          <section className="mt-5 border-t border-dashed border-gray-200 pt-4 dark:border-gray-700">
            <p className="text-sm font-semibold">Package Details</p>
            <div className="mt-3 grid gap-6 sm:grid-cols-3">
              <EditField id={fieldId("qty")} label="Qty">
                <Input
                  id={fieldId("qty")}
                  className="w-24 text-center"
                  type="number"
                  value={rowInput.qty}
                  onChange={(e) =>
                    setRowInput((prev) => ({
                      ...prev,
                      qty: parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  onBlur={() => saveRow("qty")}
                />
              </EditField>
              <EditField id={fieldId("qty-per-carton")} label="Qty/CTN">
                <Input
                  id={fieldId("qty-per-carton")}
                  className="w-24 text-center"
                  type="number"
                  value={rowInput.qtyPerCarton}
                  onChange={(e) =>
                    setRowInput((prev) => ({
                      ...prev,
                      qtyPerCarton: parseInt(e.target.value, 10) || 0,
                    }))
                  }
                  onBlur={() => saveRow("qtyPerCarton")}
                />
              </EditField>
              <div className="flex items-center gap-2">
                <span className="shrink-0 text-sm text-gray-600 sm:min-w-[4.5rem] dark:text-gray-300">
                  CTN
                </span>
                <span className="text-sm font-semibold tabular-nums">
                  {row.totalCarton}
                </span>
              </div>
            </div>

            <div className="mt-3 grid gap-6 sm:grid-cols-3">
              <EditField id={fieldId("net-weight")} label="N.W./CTN">
                <Input
                  id={fieldId("net-weight")}
                  className="w-24 text-center"
                  type="number"
                  value={rowInput.netWeight}
                  onChange={(e) =>
                    setRowInput((prev) => ({
                      ...prev,
                      netWeight: parseFloat(e.target.value) || 0,
                    }))
                  }
                  onBlur={() => saveRow("netWeight")}
                />
              </EditField>
              <EditField id={fieldId("gross-weight")} label="G.W./CTN">
                <Input
                  id={fieldId("gross-weight")}
                  className="w-24 text-center"
                  type="number"
                  value={rowInput.grossWeight}
                  onChange={(e) =>
                    setRowInput((prev) => ({
                      ...prev,
                      grossWeight: parseFloat(e.target.value) || 0,
                    }))
                  }
                  onBlur={() => saveRow("grossWeight")}
                />
              </EditField>
              {isSpecial ? (
                <div className="flex items-center gap-2">
                  <span className="shrink-0 text-sm text-gray-600 sm:min-w-[4.5rem] dark:text-gray-300">
                    CBM/CTN
                  </span>
                  <span className="text-sm font-semibold tabular-nums">
                    {row.cbm}
                  </span>
                </div>
              ) : (
                <EditField id={fieldId("cbm")} label="CBM/CTN">
                  <Input
                    id={fieldId("cbm")}
                    className="w-24 text-center"
                    type="number"
                    value={rowInput.cbm}
                    onChange={(e) =>
                      setRowInput((prev) => ({
                        ...prev,
                        cbm: parseFloat(e.target.value) || 0,
                      }))
                    }
                    onBlur={() => saveRow("cbm")}
                  />
                </EditField>
              )}
            </div>

            {isSpecial && (
              <div className="mt-3">
                <div className="flex min-w-0 flex-col items-stretch gap-1 sm:flex-row sm:items-center sm:gap-2">
                  <div className="shrink-0 text-sm font-medium text-gray-600 sm:min-w-[4.5rem] dark:text-gray-300">
                    Outer CTN Size
                  </div>
                  <div className="min-w-0 overflow-x-auto pb-1 sm:flex-1">
                    <CubeSizeInput
                      idPrefix={fieldId("outer-carton")}
                      label="Outer carton size"
                      x={rowInput.outerCartonX}
                      y={rowInput.outerCartonY}
                      z={rowInput.outerCartonZ}
                      unit="cm"
                      onChange={({ x, y, z }) => {
                        const outerCartonX = parseFloat(x) || 0;
                        const outerCartonY = parseFloat(y) || 0;
                        const outerCartonZ = parseFloat(z) || 0;
                        setRowInput((prev) => ({
                          ...prev,
                          outerCartonX,
                          outerCartonY,
                          outerCartonZ,
                        }));
                      }}
                      onBlur={() =>
                        saveRow([
                          "outerCartonX",
                          "outerCartonY",
                          "outerCartonZ",
                        ])
                      }
                    />
                  </div>
                </div>
              </div>
            )}

            <div className="mt-4 grid gap-6 sm:grid-cols-3">
              <div className="flex items-center gap-2">
                <span className="shrink-0 text-sm text-gray-600 sm:min-w-[4.5rem] dark:text-gray-300">
                  N.W.
                </span>
                <span className="text-sm font-semibold tabular-nums">
                  {parseFloat(row.totalNetWeight).toFixed(2)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="shrink-0 text-sm text-gray-600 sm:min-w-[4.5rem] dark:text-gray-300">
                  G.W.
                </span>
                <span className="text-sm font-semibold tabular-nums">
                  {parseFloat(row.totalGrossWeight).toFixed(2)}
                </span>
              </div>
              <div className="flex items-center gap-2">
                <span className="shrink-0 text-sm text-gray-600 sm:min-w-[4.5rem] dark:text-gray-300">
                  CBM
                </span>
                <span className="text-sm font-semibold tabular-nums">
                  {parseFloat(row.totalCbm).toFixed(3)}
                </span>
              </div>
            </div>
          </section>

          {isSpecial && (
            <section className="mt-5 border-t border-dashed border-gray-200 pt-4 dark:border-gray-700">
              <p className="text-sm font-semibold">Customs Info</p>

              <div className="mt-3 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
                <EditField id={fieldId("customs-name")} label="Name">
                  <Input
                    id={fieldId("customs-name")}
                    className="w-40"
                    value={rowInput.customsName}
                    onChange={(e) =>
                      setRowInput((prev) => ({
                        ...prev,
                        customsName: e.target.value,
                      }))
                    }
                    onBlur={() => saveRow("customsName")}
                  />
                </EditField>
                <EditField id={fieldId("customs-brand")} label="Brand">
                  <Input
                    id={fieldId("customs-brand")}
                    className="w-40"
                    value={rowInput.customsBrand}
                    onChange={(e) =>
                      setRowInput((prev) => ({
                        ...prev,
                        customsBrand: e.target.value,
                      }))
                    }
                    onBlur={() => saveRow("customsBrand")}
                  />
                </EditField>
                <EditField id={fieldId("customs-usage")} label="Usage">
                  <Input
                    id={fieldId("customs-usage")}
                    className="w-40"
                    value={rowInput.customsUsage}
                    onChange={(e) =>
                      setRowInput((prev) => ({
                        ...prev,
                        customsUsage: e.target.value,
                      }))
                    }
                    onBlur={() => saveRow("customsUsage")}
                  />
                </EditField>
                <EditField id={fieldId("customs-value")} label="Value">
                  <Input
                    id={fieldId("customs-value")}
                    className="w-24"
                    type="number"
                    value={rowInput.customsValue}
                    onChange={(e) =>
                      setRowInput((prev) => ({
                        ...prev,
                        customsValue: parseFloat(e.target.value) || 0,
                      }))
                    }
                    onBlur={() => saveRow("customsValue")}
                  />
                </EditField>
                <EditField
                  id={fieldId("customs-size")}
                  label="Size"
                  className="md:col-span-2"
                >
                  <Input
                    id={fieldId("customs-size")}
                    className="w-full max-w-xl"
                    value={rowInput.customsSize}
                    onChange={(e) =>
                      setRowInput((prev) => ({
                        ...prev,
                        customsSize: e.target.value,
                      }))
                    }
                    onBlur={() => saveRow("customsSize")}
                  />
                </EditField>
                <EditField
                  id={fieldId("customs-material")}
                  label="Material"
                  className="md:col-span-2"
                >
                  <Input
                    id={fieldId("customs-material")}
                    className="w-full max-w-xl"
                    value={rowInput.customsMaterials}
                    onChange={(e) =>
                      setRowInput((prev) => ({
                        ...prev,
                        customsMaterials: e.target.value,
                      }))
                    }
                    onBlur={() => saveRow("customsMaterials")}
                  />
                </EditField>
              </div>
            </section>
          )}
        </div>

        {showProductEditableData ? null : (
          <div className="mt-4 flex flex-wrap items-baseline justify-between gap-x-6 gap-y-2 pr-36 lg:max-w-3xl">
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                Qty
              </span>
              <Input
                id={fieldId("qty-quick-edit")}
                className="w-20 text-center"
                type="number"
                value={rowInput.qty}
                onChange={(e) =>
                  setRowInput((prev) => ({
                    ...prev,
                    qty: parseInt(e.target.value, 10) || 0,
                  }))
                }
                onBlur={() => saveRow("qty")}
              />
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                CTN
              </span>
              <span className="text-sm font-semibold tabular-nums">
                {row.totalCarton}
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                N.W.
              </span>
              <span className="text-sm font-semibold tabular-nums">
                {parseFloat(row.totalNetWeight).toFixed(2)}
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                G.W.
              </span>
              <span className="text-sm font-semibold tabular-nums">
                {parseFloat(row.totalGrossWeight).toFixed(2)}
              </span>
            </div>
            <div className="flex items-baseline gap-2">
              <span className="text-xs font-medium text-gray-600 dark:text-gray-300">
                CBM
              </span>
              <span className="text-sm font-semibold tabular-nums">
                {parseFloat(row.totalCbm).toFixed(3)}
              </span>
            </div>
          </div>
        )}

        {validQty ? null : (
          <div
            className={`mt-2 text-red-600 font-semibold lg:hidden ${isSpecial && showProductEditableData ? "hidden" : ""}`}
          >
            This product has {itemsPerSet} items / set, {row.qty} is not a valid
            number for whole package. Please make sure the qty is correct.
          </div>
        )}
        <div className="absolute right-0 top-0 flex h-7 w-7 items-center justify-center">
          {selectionControl}
        </div>
        <div className="absolute right-0 top-9 flex w-24 flex-col gap-2 lg:right-9 lg:top-0">
          <Button
            icon={
              showProductEditableData ? (
                <PiCheckBold size={16} />
              ) : (
                <PiPencilSimpleBold size={16} />
              )
            }
            aria-label={showProductEditableData ? "Done editing" : "Edit row"}
            title={showProductEditableData ? "Done editing" : "Edit row"}
            className="h-7 w-full justify-start rounded-md px-2 transition-colors hover:bg-sky-100 active:bg-sky-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 dark:hover:bg-sky-900/60 dark:active:bg-sky-800/70"
            onClick={() => setShowProductEditableData((prev) => !prev)}
          >
            {showProductEditableData ? "Done" : "Edit"}
          </Button>
          {!isSpecial && (
            <Button
              icon={<PiArrowsClockwiseBold size={16} />}
              aria-label="Replace product"
              title="Replace product"
              className="h-7 w-full justify-start rounded-md px-2 transition-colors hover:bg-sky-100 active:bg-sky-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400 dark:hover:bg-sky-900/60 dark:active:bg-sky-800/70"
              onClick={() => changeShipmentInvoiceRowProduct(row.id)}
            >
              Replace
            </Button>
          )}
          {showProductEditableData ? null : (
            <Button
              danger
              icon={<PiTrashBold size={16} />}
              aria-label="Delete row"
              title="Delete row"
              className="h-7 w-full justify-start rounded-md px-2 transition-colors hover:bg-red-100 active:bg-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-400 dark:hover:bg-red-900/60 dark:active:bg-red-800/70"
              loading={deleteShipmentInvoiceRowRes.loading}
              onClick={() => {
                if (
                  window.confirm(
                    "Are you sure you want to delete this product?",
                  )
                ) {
                  deleteShipmentInvoiceRow({ variables: { id: row.id } });
                }
              }}
            >
              Delete
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ShipmentInvoiceRow;
